import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ComponentFactoryResolver, ComponentRef, ElementRef, HostListener, Injector, OnDestroy, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { PopoverController } from 'providers/popover-controller';
import { DestroyGuard, getElementAccumulatedScale } from 'services/browser-utils';
import { CfPopoverInterface } from './popover-interface';
import { DeferredPromise } from '@cf-shared/shared-util';
import { OverlayStackService } from 'providers/overlay-stack.service';
import { AnimationController } from 'providers/animation-controller';
import { isSamePopover } from './popover-identity';
import { PopoverListeners } from './popover-listeners';
import { LoggingService } from 'services/logging.service';
import { HandleDragTracker, HandleRelease, isBodySwipeDismiss } from './bottom-sheet-swipe';
import { BackdropFade, BackdropMode, createBackdropElement, fadeBackdrop, removeBackdropElement, resolveBackdropMode } from './popover-backdrop';

// importers name BackdropMode through this file
export { BackdropMode };

enum ScaleAnimation {
  Open = 'open',
  Close = 'close',
}

function findElementScrollableAncestor(element: HTMLElement): HTMLElement {
  if (!element) {
    return undefined
  }
  let parent = element.parentElement;
  while (parent) {
    let computedStyles = window.getComputedStyle(parent);
    // if parent has a scrollbar
    if (computedStyles.overflowY === 'auto' || computedStyles.overflowY === 'scroll') {
      return parent;
    }

    parent = parent.parentElement;
  }
  return undefined;
}

export interface PopoverImprovedOptions {
  targetFromElement: HTMLElement;
  targetToElement?: HTMLElement;
  animate?: boolean;
  hideTargetElement?: boolean;
  backdropMode?: BackdropMode; // Replaces showBackdrop
  showBackdrop?: boolean; // Deprecated, kept for compatibility
  verticalAlign?: 'center' | 'bottom' | 'top';
  horizontalAlign?: 'center' | 'left' | 'right';
  overlayPreviousPopover?: boolean; // If true, keep existing popovers open and stack on top
  disableBackdropDismiss?: boolean;
  bottomSheet?: boolean; // Display as bottom sheet with drag handle
  // Two open popovers with the same component and target count as one only when their keys
  // are equal. AlertController sets it from the alert's content; see popover-identity.ts.
  dedupeKey?: string;
}

export class PopoverImproved {
  componentRef: ComponentRef<CfPopoverInterface>;
  opts: PopoverImprovedOptions;
  animation?: Animation;
  // created with the popover, not when createPopover finishes: a repeated request for the same
  // popover is handed this promise while it is still opening, and a dismissal during opening
  // must have something to resolve (df-b9d1d3d5e4c9)
  promise: DeferredPromise<any> = DeferredPromise.defer<any>();
  dismissPromise?: DeferredPromise<boolean>;
  resizeObserver?: ResizeObserver;
  // listeners on document and the scroll container, taken off in removePopover
  listeners = new PopoverListeners();
  wrapperElement?: HTMLElement; // The wrapper element for bottom sheet mode
  backdropElement?: HTMLElement; // Per-popover backdrop element

  popoverController: PopoverController; // backpointer to the popover controller

  constructor(opts: PopoverImprovedOptions, componentRef: ComponentRef<CfPopoverInterface>, popoverController: PopoverController) {
    this.opts = opts;
    this.componentRef = componentRef;
    this.popoverController = popoverController
  }

  dismiss() {
    void this.popoverController.dismiss(this.componentRef.instance);
  };
}

@Component({
    selector: 'popover-host',
    templateUrl: 'popover-host.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PopoverHostComponent implements OnDestroy {
  private destroyGuard = new DestroyGuard();
  nextId = 0;
  public popovers: PopoverImproved[] = [];

  @ViewChild('popoversContainer', { read: ViewContainerRef })
  popoversContainer: ViewContainerRef;

  @ViewChild('cover') coverElement: ElementRef;
  readonly popoverTransitionDuration = 200;

  constructor(
    private popoverCtrl: PopoverController,
    public elementRef: ElementRef,
    private resolver: ComponentFactoryResolver,
    private overlayStack: OverlayStackService,
    private injector: Injector,
    private cdr: ChangeDetectorRef,
    private animationController: AnimationController,
    private loggingService: LoggingService,
  ) {
    //   app.viewDidEnter.subscribe(() => this.onPageChanged());
  }

  ngOnDestroy() {
    this.destroyGuard.destroy();
  }

  // Wait for CSS transition to complete on an element
  private waitForTransition(element: HTMLElement, timeoutMs: number = 1000): Promise<void> {
    return new Promise((resolve) => {
      let completed = false;

      // Timeout fallback in case transitionend doesn't fire
      const timeout = setTimeout(() => {
        if (!completed) {
          completed = true;
          element.removeEventListener('transitionend', onTransitionEnd);
          resolve();
        }
      }, timeoutMs);

      // Listen for transition end
      const onTransitionEnd = (event: TransitionEvent) => {
        // Only respond to transitions on the element itself, not bubbled events from children
        if (event.target === element && event.propertyName === 'transform') {
          if (!completed) {
            completed = true;
            clearTimeout(timeout);
            element.removeEventListener('transitionend', onTransitionEnd);
            resolve();
          }
        }
      };

      element.addEventListener('transitionend', onTransitionEnd);
    });
  }

  // adjust positions of other toasts when a new one is added or removed
  adjustPopoversPositions() {
    if (this.popovers.length === 0) {
      return;
    }

    for (let popover of this.popovers) {
      // Skip positioning for bottom sheet popovers
      if (popover.opts.bottomSheet) {
        continue;
      }
      
      let targetFromElement = popover.opts && popover.opts.targetFromElement;
      let targetToElement = popover.opts && popover.opts.targetToElement;
      if (!targetToElement) {
        targetToElement = document.body as HTMLElement;
      }
      if (targetFromElement) {
        let elem = popover.componentRef.location.nativeElement as HTMLElement;

        // translate element to the position of the target
        let targetRect = targetToElement ? targetToElement.getBoundingClientRect() : targetFromElement.getBoundingClientRect();
        let elemRect = elem.getBoundingClientRect();

        let top = targetRect.top;
        let left = targetRect.left - Math.max(elemRect.width, elem.offsetWidth) * 0.5 + targetRect.width * 0.5;
        if (targetToElement !== document.body && popover.opts.horizontalAlign && popover.opts.horizontalAlign === 'left') {
          left = targetRect.left;
        }
        if (targetToElement !== document.body && popover.opts.horizontalAlign && popover.opts.horizontalAlign === 'right') {
          left = targetRect.right - Math.max(elemRect.width, elem.offsetWidth);
        }
        if (targetToElement && (targetToElement === document.body || !popover.opts.verticalAlign || popover.opts.verticalAlign === 'center')) {
          top = targetRect.top + targetRect.height * 0.5 - Math.max(elemRect.height, elem.offsetHeight) * 0.5;
        }
        if (targetToElement !== document.body && popover.opts.verticalAlign && popover.opts.verticalAlign === 'bottom') {
          top = targetRect.bottom;
        }
        let safeAreaBottom = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-bottom')) || 0;
        let deltaBottom = top + Math.max(elemRect.height, elem.offsetHeight) - (document.body.clientHeight - safeAreaBottom);
        if (deltaBottom > 0) {
          // doesn't fit below — only flip above if the popover actually fits there
          let elemHeight = Math.max(elemRect.height, elem.offsetHeight);
          let topIfAbove: number;
          if (targetToElement !== document.body && popover.opts.verticalAlign && popover.opts.verticalAlign === 'bottom') {
            topIfAbove = targetRect.top - elemHeight;
          } else {
            topIfAbove = top - elemHeight;
          }
          // only flip if there's enough space above; otherwise keep below and let internal scroll handle it
          if (topIfAbove >= 0) {
            top = topIfAbove;
          }
        }

        let deltaRight = left + Math.max(elemRect.width, elem.offsetWidth) - document.body.clientWidth;
        if (deltaRight > 0) {
          left -= deltaRight;
        }

        let scale = getElementAccumulatedScale(targetFromElement);

        elem.style.setProperty('--popover-translate-x', `${Math.round(left)}px`);
        elem.style.setProperty('--popover-translate-y', `${Math.round(top)}px`);
        elem.style.setProperty('--popover-scale-x', `${scale.scaleX}`);
        elem.style.setProperty('--popover-scale-y', `${scale.scaleY}`);
      }
    }
  }

  ngAfterViewInit() {
    this.popoverCtrl.registerHost(this);
  }

  // reapply the layout when the window is resized
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    void (event);
    this.adjustPopoversPositions();
  }

  onCoverClick(e: Event) {

    if (this.popovers.length === 0) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const popover = this.popovers[this.popovers.length - 1];
    if (!popover.opts.disableBackdropDismiss) {
      void this.popoverCtrl.dismiss(popover.componentRef.instance);
    }
  }

  private wrapBottomSheet(componentEl: HTMLElement, popover: PopoverImproved): HTMLElement {
    // Create wrapper div for bottom sheet
    const wrapper = document.createElement('div');
    wrapper.className = 'bottom-sheet-wrapper';

    // Add drag handle element
    const handleWrapper = document.createElement('div');
    handleWrapper.className = 'bottom-sheet-handle-wrapper';
    const handle = document.createElement('div');
    handle.className = 'bottom-sheet-handle';
    handleWrapper.appendChild(handle);

    // the drag rule itself lives in bottom-sheet-swipe.ts; these three only move the sheet
    const beginDrag = (e: Event) => {
      // no transition while dragging, so the sheet follows the pointer smoothly
      wrapper.style.transition = 'none';
      e.preventDefault();
    };

    const followDrag = (offset: number | undefined) => {
      if (offset === undefined) return;
      // keep the horizontal centering while moving down
      wrapper.style.transform = `translateX(-50%) translateY(${offset}px)`;
    };

    const finishDrag = (release: HandleRelease | undefined, e: Event) => {
      switch (release) {
        case HandleRelease.Tap:
          e.preventDefault();
          e.stopPropagation();
          // leave the transform alone: the CSS dismiss transition takes it from here
          wrapper.style.transition = '';
          void this.popoverCtrl.dismiss(popover.componentRef.instance);
          break;
        case HandleRelease.Dismiss:
          e.preventDefault();
          e.stopPropagation();
          // slide off screen, then dismiss once the slide has run
          wrapper.style.transition = '';
          wrapper.style.transform = 'translateX(-50%) translateY(100vh)';
          setTimeout(() => {
            void this.popoverCtrl.dismiss(popover.componentRef.instance);
          }, 300);
          break;
        case HandleRelease.SnapBack:
          wrapper.style.transition = '';
          wrapper.style.transform = 'translateX(-50%) translateY(0)';
          break;
      }
    };

    // touch on the handle
    const touchDrag = new HandleDragTracker();
    handleWrapper.addEventListener('touchstart', (e) => {
      touchDrag.start(e.touches[0].clientY, Date.now());
      beginDrag(e);
    });
    handleWrapper.addEventListener('touchmove', (e) => {
      followDrag(touchDrag.move(e.touches[0].clientY));
    });
    handleWrapper.addEventListener('touchend', (e) => {
      finishDrag(touchDrag.end(e.changedTouches[0].clientY, Date.now(), () => wrapper.offsetHeight), e);
    });

    // mouse on the handle, for desktop; move and release are taken on document so a drag that
    // leaves the handle still ends
    const mouseDrag = new HandleDragTracker();
    handleWrapper.addEventListener('mousedown', (e) => {
      mouseDrag.start(e.clientY, Date.now());
      beginDrag(e);
    });
    popover.listeners.listen(document, 'mousemove', (e) => {
      followDrag(mouseDrag.move(e.clientY));
    });
    popover.listeners.listen(document, 'mouseup', (e) => {
      finishDrag(mouseDrag.end(e.clientY, Date.now(), () => wrapper.offsetHeight), e);
    });

    // Add handle to wrapper
    wrapper.appendChild(handleWrapper);

    // Add body swipe detection to the component content
    let bodyTouchStartY = 0;
    let bodyTouchStartTime = 0;

    componentEl.addEventListener('touchstart', (e) => {
      // Only track if not on an interactive element
      const target = e.target as HTMLElement;
      const isInteractive = target.closest('.roller-list') ||
                           target.closest('.roller-item') ||
                           target.closest('.roller-container') ||
                           target.closest('input') ||
                           target.closest('button') ||
                           target.closest('cs-radio');

      if (!isInteractive && e.touches.length === 1) {
        bodyTouchStartY = e.touches[0].clientY;
        bodyTouchStartTime = Date.now();
      }
    });

    componentEl.addEventListener('touchend', (e) => {
      if (bodyTouchStartY !== 0 && bodyTouchStartTime !== 0 && e.changedTouches.length === 1) {
        const touchEndY = e.changedTouches[0].clientY;
        const touchEndTime = Date.now();

        // a fast downward swipe on the body dismisses; see isBodySwipeDismiss for how it differs
        // from the handle's rule
        if (isBodySwipeDismiss(touchEndY - bodyTouchStartY, touchEndTime - bodyTouchStartTime)) {
          e.preventDefault();
          void this.popoverCtrl.dismiss(popover.componentRef.instance);
        }
      }

      // Reset tracking
      bodyTouchStartY = 0;
      bodyTouchStartTime = 0;
    });

    // Move the component element into the wrapper
    const parent = componentEl.parentElement;
    parent.insertBefore(wrapper, componentEl);
    wrapper.appendChild(componentEl);

    return wrapper;
  }

  // the fade of this popover's backdrop; only a visible backdrop fades, so this answers
  // undefined for no backdrop or an invisible one
  private fadePopoverBackdrop(popover: PopoverImproved, fade: BackdropFade): Animation | undefined {
    if (!popover.backdropElement || resolveBackdropMode(popover.opts) !== BackdropMode.Visible) {
      return undefined;
    }
    return fadeBackdrop(popover.backdropElement, fade, this.popoverTransitionDuration);
  }

  // a non-bottom-sheet popover grows out of the centre of its anchor element when it opens and
  // shrinks back into it when it closes. targetFromElement is optional (anchorless menus opened
  // from a handler rather than a tap event), and the anchor may have left the page by the time
  // the popover closes, so both directions fall back to the centre of the screen
  private animatePopoverScale(popover: PopoverImproved, direction: ScaleAnimation): Animation {
    let targetElement = popover.opts && popover.opts.targetFromElement;
    let fromX: string;
    let fromY: string;
    if (targetElement && targetElement.isConnected) {
      let targetRect = targetElement.getBoundingClientRect();
      fromX = `${targetRect.left + targetRect.width * 0.5}px`;
      fromY = `${targetRect.top + targetRect.height * 0.5}px`;
    } else {
      fromX = `${window.innerWidth * 0.5}px`;
      fromY = `${window.innerHeight * 0.5}px`;
    }

    const atAnchor = {
      translate: `${fromX} ${fromY}`,
      scale: `0 0`,
    };
    const inPlace = {
      translate: `var(--popover-translate-x) var(--popover-translate-y)`,
      scale: `var(--popover-scale-x) var(--popover-scale-y)`,
    };
    const keyframes = direction === ScaleAnimation.Open ? [atAnchor, inPlace] : [inPlace, atAnchor];

    let elem = popover.componentRef.location.nativeElement as HTMLElement;
    return elem.animate(keyframes, {
      duration: this.popoverTransitionDuration, // duration in milliseconds
      fill: 'forwards' // ensures the animation stays at the end state
    });
  }

  // reposition every open popover once per frame while any is open, so the one animating
  // tracks layout changes. The closing loop also marks for check and stops once the host is
  // destroyed; the opening loop has never done either, and this refactor keeps it that way
  private repositionEachFrame(direction: ScaleAnimation) {
    let frame = () => {
      if (this.popovers.length === 0) {
        return;
      }
      if (direction === ScaleAnimation.Close && this.destroyGuard.isDestroyed) {
        return;
      }
      this.adjustPopoversPositions();
      if (direction === ScaleAnimation.Close) {
        this.cdr.markForCheck();
      }
      requestAnimationFrame(frame);
    }

    requestAnimationFrame(frame);
  }

  async createPopover(component: Type<CfPopoverInterface>, data: any, popoverOptions: PopoverImprovedOptions) {
    if (this.popovers.length > 0) {
      for (const popover of this.popovers) {
        if (isSamePopover(popover, component, popoverOptions)) {
          return popover;
        }
      }

      if (!popoverOptions.overlayPreviousPopover) {
        for (const popover of this.popovers) {
          await this.popoverCtrl.dismiss(popover.componentRef.instance);
        }
      }
    }

    if (popoverOptions.hideTargetElement && popoverOptions.targetFromElement) {
      popoverOptions.targetFromElement.style.visibility = 'hidden';
    }

    let componentFactory = this.resolver.resolveComponentFactory(component);
    const componentRef = componentFactory.create(this.injector);
    let componentEl = componentRef.location.nativeElement as HTMLElement;
    componentEl.classList.add('popover')
    
    // Add class for bottom sheet mode
    if (popoverOptions.bottomSheet) {
      componentEl.classList.add('popover-bottom-sheet');
    }

    for (let key in data) {
      componentRef.instance[key] = data[key];
    }
    
    // Pass bottomSheet flag to component if it's in bottom sheet mode
    if (popoverOptions.bottomSheet && 'bottomSheet' in componentRef.instance) {
      componentRef.instance['bottomSheet'] = true;
    }

    const backdropMode = resolveBackdropMode(popoverOptions);

    this.popoversContainer.insert(componentRef.hostView);

    let popover = new PopoverImproved(popoverOptions, componentRef, this.popoverCtrl);

    let scrollableElement = findElementScrollableAncestor(popoverOptions.targetFromElement);
    if (scrollableElement) {
      popover.listeners.listen(scrollableElement, 'scroll', () => {
        this.adjustPopoversPositions();
      });
    }

    this.overlayStack.pushPopover(popover);

    this.popovers.push(popover);

    this.adjustPopoversPositions()

    // Calculate z-index to be above any active loading indicators
    // (mirrors the logic in loading-host that calculates z-index above popovers)
    let hostElement = this.elementRef.nativeElement as HTMLElement;
    let baseZIndex = 1002;

    // Check if there's an active loading indicator
    const loadingHost = document.querySelector('loading-host') as HTMLElement;
    if (loadingHost && loadingHost.style.display === 'block') {
      // Get the loading's z-index
      const loadingZIndex = parseInt(loadingHost.style.zIndex || '1002', 10);
      // Set popover-host z-index above the loading
      baseZIndex = loadingZIndex + 1;
    }

    hostElement.style.setProperty('z-index', String(baseZIndex));

    // Calculate z-index for this popover's backdrop and the popover itself
    // Each popover pair (backdrop + popover) gets sequential z-indexes
    const popoverIndex = this.popovers.length - 1;
    const backdropZIndex = baseZIndex + (popoverIndex * 2);
    const popoverZIndex = baseZIndex + (popoverIndex * 2) + 1;

    // Set z-index on the popover element
    componentEl.style.zIndex = String(popoverZIndex);

    // Create per-popover backdrop if needed
    if (backdropMode !== BackdropMode.None) {
      const dismissOnClick = popoverOptions.disableBackdropDismiss
        ? undefined
        : () => void this.popoverCtrl.dismiss(popover.componentRef.instance);
      popover.backdropElement = createBackdropElement(this.elementRef.nativeElement as HTMLElement, backdropMode, backdropZIndex, dismissOnClick);
    }

    // Handle bottom sheet setup
    let elementToAnimate = componentEl;
    if (popoverOptions.bottomSheet) {
      // Wrap the component in a bottom sheet container
      elementToAnimate = this.wrapBottomSheet(componentEl, popover);
      elementToAnimate.classList.add('popover-bottom-sheet');
      elementToAnimate.style.zIndex = String(popoverZIndex); // Apply z-index to wrapper
      popover.wrapperElement = elementToAnimate; // Store reference to wrapper

      if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) {
        // if the host is gone, answer the caller the way a dismissal would instead of never
        popover.promise.resolve({});
        return popover;
      }

      elementToAnimate.classList.add('bottom-sheet-visible');

      // Wait for CSS transition to complete using transitionend event
      await this.waitForTransition(elementToAnimate);

    }
    if (popover.opts.animate && !popover.animation && !popoverOptions.bottomSheet) {
      // For non-bottom-sheet popovers, scale in from the anchor while the backdrop fades in
      let backdropAnimation = this.fadePopoverBackdrop(popover, BackdropFade.In);
      popover.animation = this.animatePopoverScale(popover, ScaleAnimation.Open);
      this.repositionEachFrame(ScaleAnimation.Open);

      await this.animationController.withAnimationTracking(async () => {
        await this.animationController.safeAwaitAnimations([backdropAnimation, popover.animation]);
        if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;
        void popover.componentRef.instance.afterAnimationFinished();
      });
      // if the host was torn down during the animation, answer the caller the way a dismissal would
      if (this.destroyGuard.isDestroyed) {
        popover.promise.resolve({});
        return popover;
      }
    }

    const topPopover = this.popovers[this.popovers.length - 2];
    if (topPopover) {
      // We are adding small delay to allow this click to be processed and not immediately captured to avoid element immediately closing.
      void (async () => {
        //await sleep(100);
        let topPopoverElement = topPopover.componentRef.location.nativeElement as HTMLElement;
        topPopoverElement.addEventListener('click', (e: Event) => {
          void e;
          let nextPopover = this.popovers[this.popovers.findIndex(popover => popover === topPopover) + 1];
          if (nextPopover && !nextPopover.opts.disableBackdropDismiss) {
            void this.popoverCtrl.dismiss(componentRef.instance);
          }
        });
      })();
    }

    popover.resizeObserver = new ResizeObserver(() => {
      this.adjustPopoversPositions();
    });

    popover.resizeObserver.observe(popover.componentRef.location.nativeElement);

    if (!this.destroyGuard.isDestroyed) {
      this.cdr.markForCheck();
    }

    return popover;
  }

  async removePopover(popoverComponent: CfPopoverInterface, data?: any) {

    let popover = this.popovers.find(popover => popoverComponent === popover.componentRef.instance);
    if (!popover) {
      return;
    }

    if (popover.dismissPromise) {
      await popover.dismissPromise;
      return;
    }

    popover.dismissPromise = DeferredPromise.defer<boolean>();

    if (!data) {
      data = {};
    }

    this.overlayStack.popPopover();
    if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;

    popover.componentRef.instance.onWillDismiss(data);

    popover.promise.resolve(data);

    // Handle animations based on popover type
    if (popover.opts.bottomSheet) {
      if (popover.wrapperElement) {
        // Bottom sheet uses CSS transitions
        const wrapperEl = popover.wrapperElement;
        wrapperEl.classList.remove('bottom-sheet-visible');
        wrapperEl.classList.add('bottom-sheet-dismissing');

        // the wrapper's own transition is what is waited for below, not the backdrop's fade
        this.fadePopoverBackdrop(popover, BackdropFade.Out);

        // Wait for CSS transition to complete using transitionend event
        await this.waitForTransition(wrapperEl);

        // Remove the wrapper element from DOM after animation completes
        if (wrapperEl.parentElement) {
          wrapperEl.parentElement.removeChild(wrapperEl);
        }
      } else {
        // createPopover sets the wrapper right after registering the popover, so only a throw
        // in between can leave it unset; the sheet still closes, just without its slide-down
        this.loggingService.trackSilentProblem('popover-host.bottom-sheet-wrapper-missing', {
          // the host element's tag, because a production build minifies class names
          component: (popover.componentRef.location.nativeElement as HTMLElement)?.tagName?.toLowerCase(),
          hasBackdrop: !!popover.backdropElement,
        });
      }
    } else if (popover.animation) {
      // Regular popovers shrink back into their anchor while the backdrop fades out
      let backdropAnimation = this.fadePopoverBackdrop(popover, BackdropFade.Out);
      popover.animation = this.animatePopoverScale(popover, ScaleAnimation.Close);
      this.repositionEachFrame(ScaleAnimation.Close);

      await this.animationController.withAnimationTracking(async () => {
        await this.animationController.safeAwaitAnimations([backdropAnimation, popover.animation]);
      });
    }

    // Clean up per-popover backdrop element
    if (popover.backdropElement && removeBackdropElement(popover.backdropElement)) {
      popover.backdropElement = undefined;
    }

    if (popover.opts.hideTargetElement && popover.opts.targetFromElement) {
      popover.opts.targetFromElement.style.visibility = 'visible';
    }

    popover.resizeObserver?.disconnect();
    popover.listeners.releaseAll();

    this.popoversContainer.remove(this.popoversContainer.indexOf(popover.componentRef.hostView));
    popover.componentRef.destroy();

    this.popovers.splice(this.popovers.indexOf(popover), 1);

    // if all popovers are dismissed, reset popover-host z-index to default
    if (this.popovers.length === 0) {
      const hostElement = this.elementRef.nativeElement as HTMLElement;
      hostElement.style.removeProperty('z-index');
    }

    popover.dismissPromise.resolve(true);

    if (!this.destroyGuard.isDestroyed) {
      this.cdr.markForCheck();
    }
  }
}
